// ABOUTME: Selects the displayed user language during the language data migration.
// ABOUTME: Preserves device language support while the user language flag is disabled.
import type { DeviceInformation } from '../services/usersService/usersServiceQuery';
import { useAppSelector } from '../store';
import { isFeatureEnabled } from '../store/adminShell/selectors';
import { FeatureFlags } from './featuresFlagsTypes';

interface UserLanguagesOptions {
  language?: string;
  devicesByLanguage?: DeviceInformation[];
  readUserLanguage: boolean;
}

const formatLanguage = (language: string) => language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();

/**
 * Resolves the language preference to display, falling back to English when no source language is available.
 * @param language - The user profile language, used when the read user language flag is enabled.
 * @param devicesByLanguage - The device language counts, used when the read user language flag is disabled.
 * @param readUserLanguage - Whether to read from the user profile language instead of device languages.
 */
export const getUserLanguages = ({ language, devicesByLanguage, readUserLanguage }: UserLanguagesOptions) => {
  if (readUserLanguage) return language ? formatLanguage(language) : 'English';
  if (!devicesByLanguage?.length) return 'English';

  return [...devicesByLanguage]
    .sort((firstDevice, secondDevice) => {
      const deviceCountDifference = parseInt(secondDevice.device_count, 10) - parseInt(firstDevice.device_count, 10);
      if (deviceCountDifference !== 0) return deviceCountDifference;
      return firstDevice.rtspro_language.localeCompare(secondDevice.rtspro_language);
    })
    .map(({ rtspro_language }) => formatLanguage(rtspro_language))
    .slice(0, 3)
    .join(', ');
};

/**
 * Reads the user language feature flag and resolves the language preference to display.
 * @param language - The user profile language, used when the read user language flag is enabled.
 * @param devicesByLanguage - The device language counts, used when the read user language flag is disabled.
 */
export const useUserLanguages = (language?: string, devicesByLanguage?: DeviceInformation[]) => {
  const readUserLanguage = useAppSelector(isFeatureEnabled(FeatureFlags.READ_USER_LANGUAGE));
  return getUserLanguages({ language, devicesByLanguage, readUserLanguage });
};
